// Asset library uploads (HD-10): one dialog (a create flow) for images and ZIPs, files queued in the global Upload sheet.
// The calls are the pre-HD-10 UploadPanel's and ZipUploadPanel's (lib/assetLibrary.js): presign, PUT, confirm with the
// chosen client and the folder in view; a ZIP posts once to upload-zip with the folder. After an image is confirmed the
// page polls GET /api/assets/:id every 2 s until the analysis finishes, and refreshes the list when it completes (as the
// old upload card did). The hidden file inputs stay mounted with the page, so the dialog only chooses what they receive.
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileArchive, ImagePlus } from 'lucide-react';
import api from '../../lib/api';
import {
  IMAGE_ACCEPT, MAX_IMAGE_BYTES, confirmImage, imageTransfer, isImageFile, plural, zipTransfer,
} from '../../lib/assetLibrary';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import Select from '../../ui/Select';
import { useToast } from '../../ui/Toast';
import { clean } from '../../shell/copy';
import { enqueueUpload, useUploadDone } from '../../shell/uploads';

export function useAssetUpload({ clients, folderId, folderName, portal = false, onAnalysed, onZipDone }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [over, setOver] = useState(false);
  const imageInput = useRef(null);
  const zipInput = useRef(null);
  const polls = useRef(new Map());   // assetId → interval

  useEffect(() => () => { polls.current.forEach(clearInterval); polls.current.clear(); }, []);

  const watch = useCallback((assetId, name) => {
    if (!assetId || polls.current.has(assetId)) return;
    const t = setInterval(async () => {
      try {
        const { data: a } = await api.get(`/api/assets/${assetId}`);
        if (a.analysisStatus !== 'complete' && a.analysisStatus !== 'failed') return;
        clearInterval(polls.current.get(assetId));
        polls.current.delete(assetId);
        if (a.analysisStatus === 'complete') onAnalysed?.(a);
        else toast(`The analysis of ${clean(name)} failed. Open it to retry the analysis.`, { tone: 'warn' });
      } catch { /* keep polling, as before */ }
    }, 2000);
    polls.current.set(assetId, t);
  }, [onAnalysed, toast]);

  useUploadDone(item => {
    if (item.meta?.kind === 'asset') watch(item.result?.assetId, item.name);
    if (item.meta?.kind === 'asset-zip') {
      const s = item.result || {};
      const parts = [`${plural(s.queued || 0, 'photo')} from ${clean(item.name)} uploaded; the analysis runs in the background.`];
      if (s.over_limit) parts.push('The ZIP held more than 50 images, so the first 50 were taken.');
      if (s.skipped > 0) parts.push(`${plural(s.skipped, 'file')} skipped (not an image, or over 20 MB).`);
      toast(parts.join(' '), { tone: s.over_limit || s.skipped ? 'warn' : 'ok', duration: 10000 });
      onZipDone?.();
    }
  });

  function takeImages(list) {
    const all = [...(list || [])];
    const images = all.filter(isImageFile);
    const tooBig = images.filter(f => f.size > MAX_IMAGE_BYTES);
    const ok = images.filter(f => f.size <= MAX_IMAGE_BYTES);
    if (all.length && !images.length) { toast('Those files are not images. Choose JPEG, PNG, WebP, GIF or HEIC files.', { tone: 'warn' }); return; }
    if (tooBig.length) toast(`${tooBig.length === 1 ? clean(tooBig[0].name) : plural(tooBig.length, 'image')} over 20 MB, not uploaded.`, { tone: 'warn' });
    ok.forEach(file => enqueueUpload({
      file,
      transfer: imageTransfer,
      confirm: confirmImage({ folderId, clientId }),
      meta: { kind: 'asset', folderId: folderId || null, clientId: clientId || null },
      limit: 6,
    }));
    if (ok.length) {
      setOpen(false);
      toast(`${plural(ok.length, 'image')} uploading. Progress is in the upload sheet.`, { tone: 'info' });
    }
  }

  function takeZip(file) {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { toast('Choose a .zip file.', { tone: 'warn' }); return; }
    enqueueUpload({ file, transfer: zipTransfer(folderId), confirm: async s => s, meta: { kind: 'asset-zip', folderId: folderId || null } });
    setOpen(false);
    toast(`${clean(file.name)} uploading. Progress is in the upload sheet.`, { tone: 'info' });
  }

  const into = folderId ? clean(folderName) || 'this folder' : null;
  const element = (
    <>
      <input ref={imageInput} type="file" accept={IMAGE_ACCEPT} multiple hidden data-testid="assets-file-input"
        onChange={e => { if (e.target.files?.length) takeImages(e.target.files); e.target.value = ''; }} />
      <input ref={zipInput} type="file" accept=".zip" hidden data-testid="assets-zip-input"
        onChange={e => { const f = e.target.files?.[0]; if (f) takeZip(f); e.target.value = ''; }} />
      <Modal open={open} onClose={() => setOpen(false)} title="Upload to the asset library"
        meta={into ? `Into the folder ${into}` : 'Into the library, not in a folder'}
        footer={<Button variant="quiet" portal={portal} onClick={() => setOpen(false)}>Cancel</Button>}>
        <div className="ui-stack">
          {clients.length > 0 && (
            <Select label="Client" value={clientId} onChange={e => setClientId(e.target.value)} hint="Images take this client. A ZIP is uploaded without one."
              options={[{ value: '', label: 'No client (shared)' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
          )}
          <div className="ui-dropzone" data-drop={over || undefined}
            onDragOver={e => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={e => {
              e.preventDefault(); setOver(false);
              const files = [...e.dataTransfer.files];
              const zip = files.find(f => /\.zip$/i.test(f.name));
              if (zip && files.length === 1) takeZip(zip); else takeImages(files);
            }}>
            <span className="ui-dense ui-text-2">Drop images here, or choose them. JPEG, PNG, WebP, GIF or HEIC, up to 20 MB each. Clio describes and tags each one after it uploads.</span>
            <Button variant="primary" portal={portal} icon={ImagePlus} data-testid="assets-upload-images" onClick={() => imageInput.current?.click()}>Choose images</Button>
          </div>
          <div className="ui-dropzone">
            <span className="ui-dense ui-text-2">Or upload a ZIP of up to 50 images at once.</span>
            <Button variant="secondary" portal={portal} icon={FileArchive} data-testid="assets-upload-zip" onClick={() => zipInput.current?.click()}>Choose a ZIP</Button>
          </div>
        </div>
      </Modal>
    </>
  );
  return { openUpload: () => setOpen(true), element };
}
